import type { EditorState } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import type { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { EmojiItem } from '@core/symbol-data';

import type { EmojiEditorPluginEvents, EmojiListController, EmojiOpenOptions } from './types';
import { EmojiPluginPickerClose, EmojiPluginPickerBeforeOpen } from './types';
import type { OverlayPosition } from '../../editor/types';

export class EmojiServiceInternal {
  public currentOrigin: number | HTMLElement;

  private onSelect: (item: EmojiItem) => void;
  private preferredPickerPositions = [
    <const>{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    <const>{ originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
  ];

  constructor(
    private editorView: EditorView,
    private emojiListController: EmojiListController,
    private eventStream$: Subject<EmojiEditorPluginEvents>,
    private scroll$: Observable<void>,
    private destroy$: Observable<void>,
  ) {
    this.scroll$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (!this.emojiListController.isOpen()) return;

      const position = this.getPositionFromCurrentOrigin();
      this.emojiListController.updatePosition(position);
    });
  }

  public emojiMatchHandler(
    state: EditorState,
    match: RegExpMatchArray | null,
    start: number,
    end: number,
  ) {
    const isOpen = this.emojiListController.isOpen();

    if (!match) return this.closePicker();

    const searchValue = match[0].replace(':', '');
    if (searchValue.trim().length < 2) return this.closePicker();

    const hasResults = this.emojiListController.dataSourceHasResults('emojis', searchValue);
    if (!hasResults) return this.closePicker();

    const from = searchValue[0].match(/\s/) ? start + 1 : start;
    this.currentOrigin = from;
    if (!isOpen) {
      const position = this.editorView.coordsAtPos(from);
      this.openPicker(position, {
        searchValue: searchValue,
        preferredPositions: this.preferredPickerPositions,
        dataSources: ['emojis'],
      });
    }

    const onSelect = this.getEmojiSelectCallback(from, end);
    this.setSelectCallback(onSelect);
    this.emojiListController.update('searchValue', searchValue);
  }

  public setSelectCallback(callback: (emoji: EmojiItem) => void) {
    this.onSelect = callback;
  }

  public openPicker(position: OverlayPosition, options: EmojiOpenOptions) {
    this.eventStream$.next(new EmojiPluginPickerBeforeOpen());
    const { select$, backdropClick$ } = this.emojiListController.open(position, options);
    select$.subscribe(symbol => {
      this.onSelect(symbol.value);
      this.closePicker();
      this.editorView.focus();
    });

    backdropClick$.subscribe({
      complete: () => {
        this.eventStream$.next(new EmojiPluginPickerClose());
        this.editorView.focus();
      },
    });
    return { select$, backdropClick$ };
  }

  public closePicker() {
    this.emojiListController.close();
    this.eventStream$.next(new EmojiPluginPickerClose());
  }

  public getEmojiSelectCallback = (from: number, to: number) => (emoji: EmojiItem) => {
    const { state } = this.editorView;
    const { tr } = state;
    let suffix = ' ';
    try {
      const nextIsSpace = state.doc.textBetween(to, to + 1) === ' ';
      suffix = nextIsSpace ? '' : ' ';
    } catch (e) {
      /* empty */
    }
    const emojiNode = state.schema.text(emoji.symbol + suffix);
    tr.replaceWith(from, to, emojiNode);
    this.editorView.dispatch(tr);

    this.closePicker();
    this.editorView.focus();
  };

  private getPositionFromCurrentOrigin() {
    if (this.currentOrigin instanceof HTMLElement) {
      return this.currentOrigin.getBoundingClientRect();
    } else {
      return this.editorView.coordsAtPos(this.currentOrigin);
    }
  }
}
